import { BorderRadiusCss } from '@tvg/fuse-utils';
import styled from 'styled-components';
import { Button } from '../../fuse-buttons/src/buttons';
import { elevation } from '../../fuse-elevation/src/elevation';
import { Link } from '../../fuse-link/src/links';
import {
	GridContainer,
	GridItem,
	GridRow,
	gridConfig as breakpoints,
} from '../../fuse-responsive-grid/src/grid';

// Box-shadow size from elevation to be used as background size to emulate sticky header drop shadow in modal body
const xxsShadowSize = elevation.shadow[16].split(/\s/)[2];
const mShadowSize = elevation.shadow[24].split(/\s/)[2];
const xlShadowSize = elevation.shadow[24].split(/\s/)[2];
// Modal Styles Config
const stylesConfig = {
	xxs: {
		maxHeightMargin: 'var(--spacing-global-space5)',
		bodyPadding:
			'var(--spacing-global-space6) var(--spacing-global-space5) var(--spacing-global-space7)',
		bodyBackground: `linear-gradient(var(--color-functional-panelLight), 70%, rgba(255,255,255,0)) local no-repeat, linear-gradient(rgba(0,0,0,0.2), 2px, rgba(255,255,255,0)) scroll no-repeat`,
		bodyBackgroundSize: `100% ${xxsShadowSize}, 100% ${xxsShadowSize}`,
	},
	m: {
		maxHeightMargin: 'var(--spacing-global-space6)',
		bodyPadding:
			'var(--spacing-global-space7) var(--spacing-global-space6) var(--spacing-global-space8)',
		bodyBackground: `linear-gradient(var(--color-functional-panelLight), 70%, rgba(255,255,255,0)) local no-repeat, linear-gradient(rgba(0,0,0,0.2), 2px, rgba(255,255,255,0)) scroll no-repeat`,
		bodyBackgroundSize: `100% ${mShadowSize}, 100% ${mShadowSize}`,
	},
	xl: {
		maxHeightMargin: 'var(--spacing-global-space7)',
		bodyPadding:
			'var(--spacing-global-space7) var(--spacing-global-space7) var(--spacing-global-space8)',
		bodyBackground: `linear-gradient(var(--color-functional-panelLight), 70%, rgba(255,255,255,0)) local no-repeat, linear-gradient(rgba(0,0,0,0.2), 2px, rgba(255,255,255,0)) scroll no-repeat`,
		bodyBackgroundSize: `100% ${xlShadowSize}, 100% ${xlShadowSize}`,
	},
};

const StyledModal = styled.div.attrs({
	className: 'fuse-modal',
})`
	${({ open, zIndex }) => `
		bottom: 0;
		left: 0;
		opacity: 0;
		pointer-events: none;
		position: fixed;
		right: 0;
		top: 0;
		transition: all 0.2s ease-out;
		visibility: ${open ? 'visible' : 'hidden'};
		z-index: ${zIndex || ``};
		overflow: auto;
		&.fuse-modal--show {
			opacity: 1;
			pointer-events: visible;
			transition: all 0.2s ease-in;
		}
	`}
`;

const StyledModalOverlay = styled.div.attrs({
	className: 'fuse-modal__overlay',
	'data-testid': 'fuse-modal__overlay',
})`
	align-items: center;
	background-color: rgba(0, 0, 0, 0.2);
	height: 100%;
	width: 100%;
	overflow: auto;
`;

const StyledModalGridContainer = styled(GridContainer).attrs({
	className: 'fuse-modal__grid-container',
	'data-testid': 'fuse-grid-container',
})`
	height: 100%;
`;

const StyledModalGridRow = styled(GridRow).attrs({
	className: 'fuse-modal__grid-row',
	'data-testid': 'fuse-grid-row',
})``;

const StyledModalGridItem = styled(GridItem).attrs({
	className: 'fuse-modal__grid-item',
	'data-testid': 'fuse-grid-item',
})``;

const StyledModalContainer = styled.div.attrs({
	className: 'fuse-modal__content',
	'data-testid': 'fuse-modal__content',
})`
	${BorderRadiusCss('l')};
	box-shadow: var(--elevation-shadow-l);
	display: flex;
	flex-direction: column;
	max-height: calc(
		(var(--vh, 1vh) * 100) - (${stylesConfig.xxs.maxHeightMargin} * 2)
	); /* Using css var --vh for mobile and tablet due to browser top and bottom bar issue */
	/* overflow: auto; */
	position: relative;
	width: 100%;

	&:focus {
		outline: none;
	}

	@media only screen and (min-width: ${breakpoints.m.minWidth}) {
		max-height: calc(
			(var(--vh, 1vh) * 100) - (${stylesConfig.xl.maxHeightMargin} * 2)
		); /* Using css var --vh for mobile and tablet due to browser top and bottom bar issue */
	}

	@media only screen and (min-width: ${breakpoints.xl.minWidth}) {
		max-height: calc(100vh - (${stylesConfig.m.maxHeightMargin} * 2));
	}
`;

const StyledModalLink = styled(Link)
.withConfig({ shouldForwardProp: (prop) => prop !== 'linkCenterStacked' })
.attrs({
	className: 'fuse-modal__link',
})`
	align-items: center;
	display: flex;
	justify-content: center;
	width: 100%;
	@media screen and (min-width: ${breakpoints.m.minWidth}) {
		width: ${({ linkCenterStacked }) => (linkCenterStacked ? `auto` : `unset`)};
	}
`;

const StyledModalButton = styled(Button)
.withConfig({ shouldForwardProp: (prop) => prop !== 'linkCenterStacked' })
.attrs({
	className: 'fuse-modal__button',
})`
	align-items: center;
	display: flex;
	justify-content: center;

	width: ${({ linkCenterStacked }) => (linkCenterStacked ? `100%` : `unset`)};
`;

const StyledModalLinkContainer = styled.div
.withConfig({ shouldForwardProp: (prop) => !['linkCenterAlignment', 'linkCenterStacked'].includes(prop),})
.attrs({
	className: 'fuse-modal__linkcontainer',
	'data-testid': 'fuse-modal__linkcontainer',
})`
	width: 100%;
	gap: var(--spacing-global-space4);
	display: flex;
	flex-direction: column-reverse;

	 @media screen and (min-width: ${breakpoints.m.minWidth}) {
		width: ${({ linkCenterStacked }) => (linkCenterStacked ? `fit-content` : `unset`)};
		flex-direction: ${({ linkCenterStacked }) => (linkCenterStacked ? `column-reverse` : `row`)};
	}
	@media only screen and (min-width: ${breakpoints.xl.minWidth}) {
			align-items: center;
	}

`;

const StyledModalFooter = styled.div
.withConfig({ shouldForwardProp: (prop) => !['linkCenterAlignment', 'linkCenterStacked'].includes(prop),})
.attrs({
	className: 'fuse-modal__footer',
})`
	justify-content: ${({ linkCenterAlignment, linkCenterStacked }) =>	linkCenterAlignment || linkCenterStacked ? `center` : `flex-end`};
	padding: var(--spacing-global-space7) var(--spacing-global-space6) var(--spacing-global-space8);
	width: 100%;
	height: fit-content;
	display: flex;

	@media screen and (min-width: ${breakpoints.xxs.minWidth}) {
		background: ${stylesConfig.xxs.bodyBackground};
		background-color: var(--color-functional-panelLight);
		background-size: ${stylesConfig.xxs.bodyBackgroundSize};
	}

	@media screen and (min-width: ${breakpoints.m.minWidth}) {
		background: ${stylesConfig.m.bodyBackground};
		background-color: var(--color-functional-panelLight);
		background-size: ${stylesConfig.m.bodyBackgroundSize};
	}

	@media only screen and (min-width: ${breakpoints.xl.minWidth}) {
		background: ${stylesConfig.xl.bodyBackground};
		background-color: var(--color-functional-panelLight);
		background-size: ${stylesConfig.xl.bodyBackgroundSize};
	}
`;

const StyledModalBody = styled.div.attrs({
	className: 'fuse-modal__body',
	'data-testid': 'fuse-modal__body',
})`
	min-height: 50%;
	${({ hasPadding }) => `
        overflow-x: visable;
        overflow-y: auto;

        padding: ${hasPadding ? stylesConfig.xxs.bodyPadding : ``};
        /* Creates shadow at top of modal body to emulate sticky header box-shadow when body is scrolled */
        background: ${stylesConfig.xxs.bodyBackground};
        background-color: var(--color-functional-panelLight);
        background-size: ${stylesConfig.xxs.bodyBackgroundSize};

        @media only screen and (min-width: ${breakpoints.m.minWidth}) {
            background: ${stylesConfig.m.bodyBackground};
            background-color: var(--color-functional-panelLight);
            background-size: ${stylesConfig.m.bodyBackgroundSize};
            padding: ${hasPadding ? stylesConfig.m.bodyPadding : ``};
        }

        @media only screen and (min-width: ${breakpoints.xl.minWidth}) {
            background: ${stylesConfig.xl.bodyBackground};
            background-color: var(--color-functional-panelLight);
            background-size: ${stylesConfig.xl.bodyBackgroundSize};
            padding: ${hasPadding ? stylesConfig.xl.bodyPadding : ``};
        }
    `}
`;

export {
	StyledModal,
	StyledModalBody,
	StyledModalButton,
	StyledModalContainer,
	StyledModalFooter,
	StyledModalGridContainer,
	StyledModalGridRow,
	StyledModalGridItem,
	StyledModalLink,
	StyledModalLinkContainer,
	StyledModalOverlay,
};
